import { Configuration, OpenAIApi } from "openai";

const configuration = new Configuration({
  apiKey: process.env.OPENAI_API_KEY,
});
const openai = new OpenAIApi(configuration);

/**
 *
 * @returns
 *
 * The function for the call should make sure to specify
 * that it shouldn't include the word of the section that it's involved in.
 *
 * I.e. If it's a list about young adult fiction, it shouldn't include
 * the words young or adult.
 *
 */

function generatePrompt(books: any[], categoryName: string): string {
  const formattedBooks = books
    .map((book, index) => {
      return `Book ${index + 1}: ${book.title} by ${book.author}`;
    })
    .join("\n");

  return `
    Please sort the following books into exactly 3 categories. 
    The categories do not need to contain the same number of books, but there must be at least one book in each category.
    The category names should not include the words of any of the titles, nor the following category name (${categoryName}), nor any book categories you could find on Amazon.
    The category names should be more than one word long but less than six.
    Describe the categories in names like you were a PHD student whose dissertation is dependent on how smart and esoteric they sound.
    The answer should follow the format: 
    [Category 1: {name}}]
    Book A: [Book Title] by [Author]
    Here are the books to categorize:
    ${formattedBooks}`;
}

interface BookInterface {
  title: string;
  author: string;
}

export async function sortBooks(
  books: BookInterface[],
  bookCategories: string[]
): Promise<Category[]> {
  console.log(books);
  const categoryNamesToAvoid = bookCategories.join(", ");

  const prompt = generatePrompt(books, categoryNamesToAvoid);

  const response = await openai.createChatCompletion({
    model: "gpt-3.5-turbo",
    messages: [
      {
        role: "user",
        content: prompt,
      },
    ],
    temperature: 1.2,
    max_tokens: 500,
  });

  const choices = response.data.choices[0].message?.content.split("\n\n");

  const categories = formatCategories(choices);

  if (!validateAllCategoriesHaveOneBook(categories)) {
    throw new Error("Not all categories have at least one book");
  }

  return categories;
}

function formatBook(bookLine: string): OpenAIBook {
  const [, number, title, author] =
    bookLine.match(/^Book (\d+): (.+) by (.+)$/) || [];

  return {
    number: parseInt(number),
    title: title,
    author: author,
  };
}

function formatCategory(category: string): Category {
  const [categoryHeader, ...booksLines] = category.split("\n");
  const [, categoryName] =
    categoryHeader.match(/^\[Category \d+: (.*)\]$/) || [];

  const books = booksLines
    .map((bookLine) => formatBook(bookLine))
    .filter((book) => book.number);

  return {
    categoryName: categoryName.replace("]", ""),
    books: books,
  };
}

function formatCategories(output: string[] | undefined): Category[] {
  if (!output) return [];

  return output.map((category) => formatCategory(category));
}

function validateAllCategoriesHaveOneBook(categories: Category[]): boolean {
  return categories.every((category) => category.books.length > 0);
}
